import React, {
    useEffect
} from 'react';
import {
    Outlet, useLocation
} from 'react-router-dom';
import {
    RouteErrorBoundary
} from 'shared/components/ErrorBoundary';
import DSPlatformBar from './DSPlatformBar';
import DEStudioSubStrip from './DEStudioSubStrip';
import StudioAgentDock from './agent/StudioAgentDock';
import './ui/tokens.css';
import './ui/chrome-overrides.css';

/**
 * DEStudioShell — top-level container for the new builder.
 * Per A-0012.02 SPEC §2.1.
 *
 * Mounts the sub-strip + an <Outlet/> for the 4 sub-pages
 * (Unified Builder / Library / Activity / Knowledge).
 *
 * Hides the sub-strip on the index/gate route (`.../de-studio` exactly) so
 * the mode-select landing page renders edge-to-edge.
 */
export default function DEStudioShell() {
    const {
        pathname
    } = useLocation();
    // Detect "is on index/gate" — pathname ends with /de-studio with no
    // trailing sub-route segment.
    const onGate = /\/de-studio\/?$/.test(pathname);

    // Toggle chrome-overrides on body so the global header + sub-org sidebar
    // compress while inside DE Studio, then return to normal on exit.
    useEffect(() => {
        document.body.setAttribute('data-on-destudio', 'true');
        // Mirror the theme attribute on <body> too — Radix Dialog / Popover /
        // Select content portals to document.body, escaping the wrapper. With
        // body carrying data-theme=destudio, every descendant (portaled or not)
        // inherits the DS tokens AND our `[data-theme="destudio"] .ds-*` rules.
        document.body.setAttribute('data-theme', 'destudio');
        return () => {
            document.body.removeAttribute('data-on-destudio');
            document.body.removeAttribute('data-theme');
        };
    }, []);

    return (
        <div data-theme="destudio" className="flex h-full w-full flex-col" style={{
            background: 'var(--ds-bg)'
        }}>
            {/* Single platform bar — always visible. Carries brand, org/sub-org
                switchers (with settings tucked into their dropdowns), product
                tabs, theme toggle and user menu. Replaces the previous global
                header + sub-org sidebar. */}
            <DSPlatformBar />
            {/* Section sub-strip — only on DE Studio's inner pages. */}
            {!onGate && <DEStudioSubStrip />}
            <div className="flex-1 overflow-auto">
                {/* Tier 2 — DE Studio pages render stored assistant/opcode
                    documents. A malformed one must not take the platform bar
                    and sub-strip down with it. */}
                <RouteErrorBoundary name="de-studio-outlet">
                    <Outlet />
                </RouteErrorBoundary>
            </div>

            {/* BEXI agent — FAB + dockable/expandable panel. Not on the gate page. */}
            {!onGate && <StudioAgentDock />}
        </div>
    );
}
